import { useReducer, useEffect, useCallback, useState, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, UNSAFE_NavigationContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ClipLoader } from 'react-spinners';
import { notify } from '../../utils/notifications';
import api from '../../api/api';
import MetadataSection from './sections/MetadataSection';
import ExtractionSection from './sections/ExtractionSection';
import RedFlagsSection from './sections/RedFlagsSection';
import DocumentTemplatesSection from './sections/DocumentTemplatesSection';
import PreviewSection from './sections/PreviewSection';
import './WorkflowEditor.css';

// ─── Reducer ──────────────────────────────────────────────────────
const initialState = {
  config: null,
  allFields: [],
  allFlags: [],
  allTemplates: [],
  allConfigs: [],
  dirty: false,
  saving: false,
  loading: true,
  error: null,
  activeTab: 'metadata',
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_DATA':
      return {
        ...state,
        config: action.config,
        allFields: action.allFields,
        allFlags: action.allFlags,
        allTemplates: action.allTemplates,
        allConfigs: action.allConfigs,
        loading: false,
        error: null,
      };
    case 'SET_ERROR':
      return { ...state, loading: false, error: action.error };
    case 'SET_TAB':
      return { ...state, activeTab: action.tab };
    case 'UPDATE_CONFIG':
      return { ...state, config: { ...state.config, ...action.updates }, dirty: true };
    case 'SET_SAVING':
      return { ...state, saving: action.saving };
    case 'MARK_CLEAN':
      return { ...state, dirty: false };
    case 'RESET':
      return { ...state, config: action.config, dirty: false };

    // Extraction group mutations
    case 'ADD_GROUP':
      return {
        ...state,
        dirty: true,
        config: {
          ...state.config,
          extraction: [
            ...(state.config.extraction || []),
            {
              group_name: action.groupName,
              order: (state.config.extraction || []).length + 1,
              field_refs: [],
            },
          ],
        },
      };
    case 'REMOVE_GROUP':
      return {
        ...state,
        dirty: true,
        config: {
          ...state.config,
          extraction: (state.config.extraction || [])
            .filter((_, i) => i !== action.index)
            .map((g, i) => ({ ...g, order: i + 1 })),
        },
      };
    case 'REORDER_GROUPS':
      return {
        ...state,
        dirty: true,
        config: {
          ...state.config,
          extraction: action.groups.map((g, i) => ({ ...g, order: i + 1 })),
        },
      };
    case 'ADD_FIELD_TO_GROUP': {
      const extraction = [...(state.config.extraction || [])];
      extraction[action.groupIndex] = {
        ...extraction[action.groupIndex],
        field_refs: [...extraction[action.groupIndex].field_refs, action.fieldId],
      };
      return { ...state, dirty: true, config: { ...state.config, extraction } };
    }
    case 'REMOVE_FIELD_FROM_GROUP': {
      const extraction = [...(state.config.extraction || [])];
      extraction[action.groupIndex] = {
        ...extraction[action.groupIndex],
        field_refs: extraction[action.groupIndex].field_refs.filter((_, i) => i !== action.fieldIndex),
      };
      return { ...state, dirty: true, config: { ...state.config, extraction } };
    }
    case 'REORDER_FIELDS_IN_GROUP': {
      const extraction = [...(state.config.extraction || [])];
      extraction[action.groupIndex] = {
        ...extraction[action.groupIndex],
        field_refs: action.fieldRefs,
      };
      return { ...state, dirty: true, config: { ...state.config, extraction } };
    }

    // Red flag mutations
    case 'ADD_CATEGORY':
      return {
        ...state,
        dirty: true,
        config: {
          ...state.config,
          red_flags: [
            ...(state.config.red_flags || []),
            {
              category_name: action.categoryName,
              order: (state.config.red_flags || []).length + 1,
              flag_refs: [],
            },
          ],
        },
      };
    case 'REMOVE_CATEGORY':
      return {
        ...state,
        dirty: true,
        config: {
          ...state.config,
          red_flags: (state.config.red_flags || [])
            .filter((_, i) => i !== action.index)
            .map((c, i) => ({ ...c, order: i + 1 })),
        },
      };
    case 'REORDER_CATEGORIES':
      return {
        ...state,
        dirty: true,
        config: {
          ...state.config,
          red_flags: action.categories.map((c, i) => ({ ...c, order: i + 1 })),
        },
      };
    case 'ADD_FLAG_TO_CATEGORY': {
      const redFlags = [...(state.config.red_flags || [])];
      redFlags[action.categoryIndex] = {
        ...redFlags[action.categoryIndex],
        flag_refs: [...redFlags[action.categoryIndex].flag_refs, action.flagId],
      };
      return { ...state, dirty: true, config: { ...state.config, red_flags: redFlags } };
    }
    case 'REMOVE_FLAG_FROM_CATEGORY': {
      const redFlags = [...(state.config.red_flags || [])];
      redFlags[action.categoryIndex] = {
        ...redFlags[action.categoryIndex],
        flag_refs: redFlags[action.categoryIndex].flag_refs.filter((_, i) => i !== action.flagIndex),
      };
      return { ...state, dirty: true, config: { ...state.config, red_flags: redFlags } };
    }

    // Document template mutations
    case 'ADD_TEMPLATE_REF':
      return {
        ...state,
        dirty: true,
        config: {
          ...state.config,
          document_refs: [...(state.config.document_refs || []), action.templateId],
        },
      };
    case 'REMOVE_TEMPLATE_REF':
      return {
        ...state,
        dirty: true,
        config: {
          ...state.config,
          document_refs: (state.config.document_refs || []).filter((_, i) => i !== action.index),
        },
      };
    case 'REORDER_TEMPLATE_REFS':
      return {
        ...state,
        dirty: true,
        config: { ...state.config, document_refs: action.templateRefs },
      };

    // Refresh entities after inline edits
    case 'UPDATE_FIELD':
      return {
        ...state,
        allFields: state.allFields.map((f) => (f.id === action.field.id ? action.field : f)),
      };
    case 'ADD_FIELD':
      return { ...state, allFields: [...state.allFields, action.field] };
    case 'UPDATE_FLAG':
      return {
        ...state,
        allFlags: state.allFlags.map((f) => (f.id === action.flag.id ? action.flag : f)),
      };
    case 'ADD_FLAG':
      return { ...state, allFlags: [...state.allFlags, action.flag] };
    case 'UPDATE_TEMPLATE':
      return {
        ...state,
        allTemplates: state.allTemplates.map((t) => (t.id === action.template.id ? action.template : t)),
      };
    case 'ADD_TEMPLATE':
      return { ...state, allTemplates: [...state.allTemplates, action.template] };

    default:
      return state;
  }
}

// ─── Tabs ─────────────────────────────────────────────────────────
const TABS = [
  { id: 'metadata', label: 'Metadata' },
  { id: 'extraction', label: 'Extraction', countKey: 'extraction' },
  { id: 'redflags', label: 'Red Flags', countKey: 'red_flags' },
  { id: 'documents', label: 'Documents', countKey: 'document_refs' },
  { id: 'preview', label: 'Preview' },
];

// ─── Component ────────────────────────────────────────────────────
function WorkflowEditor() {
  const { configId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [configRes, fieldsRes, flagsRes, templatesRes, allConfigsRes] = await Promise.all([
        api.get(`/admin/facility-configs/${configId}`),
        api.get('/admin/extraction-fields'),
        api.get('/admin/red-flags'),
        api.get('/admin/document-templates'),
        api.get('/admin/facility-configs'),
      ]);
      dispatch({
        type: 'SET_DATA',
        config: configRes.data,
        allFields: fieldsRes.data,
        allFlags: flagsRes.data,
        allTemplates: templatesRes.data,
        allConfigs: allConfigsRes.data,
      });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: 'Failed to load workflow configuration' });
      notify.error('Failed to load workflow');
    }
  }, [configId]);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin, fetchData]);

  // Block all in-app navigation when dirty using history.listen
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const pendingRetry = useRef(null);

  useEffect(() => {
    if (!state.dirty) return;

    const push = navigator.push;
    const replace = navigator.replace;

    navigator.push = (...args) => {
      pendingRetry.current = () => { navigator.push = push; push.apply(navigator, args); };
      setShowDiscardConfirm(true);
    };
    navigator.replace = (...args) => {
      pendingRetry.current = () => { navigator.replace = replace; replace.apply(navigator, args); };
      setShowDiscardConfirm(true);
    };

    return () => {
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [state.dirty, navigator]);

  // Close confirm dialog on Escape
  useEffect(() => {
    if (!showDiscardConfirm) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setShowDiscardConfirm(false);
        pendingRetry.current = null;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showDiscardConfirm]);

  const handleSave = async () => {
    dispatch({ type: 'SET_SAVING', saving: true });
    try {
      const { id, name, description, system_instruction, active, extraction, red_flags, document_refs } = state.config;
      await api.put(`/admin/facility-configs/${configId}`, {
        name,
        description,
        system_instruction,
        active,
        extraction: extraction || [],
        red_flags: red_flags || [],
        document_refs: document_refs || [],
      });
      dispatch({ type: 'MARK_CLEAN' });
      notify.success('Configuration saved');
    } catch (err) {
      notify.error('Failed to save configuration');
    } finally {
      dispatch({ type: 'SET_SAVING', saving: false });
    }
  };

  const handleDiscardClick = () => {
    if (state.dirty) {
      setShowDiscardConfirm(true);
    } else {
      fetchData();
    }
  };

  const handleConfirmLeave = () => {
    setShowDiscardConfirm(false);
    dispatch({ type: 'MARK_CLEAN' });
    if (pendingRetry.current) {
      const retry = pendingRetry.current;
      pendingRetry.current = null;
      retry();
    } else {
      // Discard button (not a navigation) — reload data
      fetchData();
    }
  };

  const handleCancelLeave = () => {
    setShowDiscardConfirm(false);
    pendingRetry.current = null;
  };

  const getTabCount = (tab) => {
    if (!state.config || !tab.countKey) return null;
    const data = state.config[tab.countKey];
    return Array.isArray(data) ? data.length : null;
  };

  if (!isAdmin) {
    return <div className="wfe-page"><p>Admin access required.</p></div>;
  }

  if (state.loading) {
    return (
      <div className="wfe-page">
        <div className="wfe-loading"><ClipLoader size={28} color="#0D9488" /></div>
      </div>
    );
  }

  if (state.error || !state.config) {
    return (
      <div className="wfe-page">
        <p className="wfe-error">{state.error || 'Configuration not found'}</p>
      </div>
    );
  }

  return (
    <div className="wfe-page">
      {/* Back link */}
      <button className="wfe-back" onClick={() => navigate('/admin/workflows')}>
        &larr; Back to Workflows
      </button>

      {/* Header */}
      <div className="wfe-header">
        <div>
          <div className="wfe-title-row">
            <h1 className="wfe-title">{state.config.name}</h1>
            <span className="wfe-id">{state.config.id}</span>
            <span className={`wfe-pill ${state.config.active !== false ? 'wfe-pill-active' : 'wfe-pill-inactive'}`}>
              {state.config.active !== false ? 'Active' : 'Draft'}
            </span>
          </div>
        </div>
        <div className="wfe-actions">
          {state.dirty && (
            <button className="wfe-btn-discard" onClick={handleDiscardClick}>Discard</button>
          )}
          <button
            className="wfe-btn-save"
            onClick={handleSave}
            disabled={state.saving || !state.dirty}
          >
            {state.saving ? (
              <ClipLoader size={14} color="#fff" />
            ) : (
              <>
                {state.dirty && <span className="wfe-unsaved-dot" />}
                Save Config
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="wfe-tabs">
        {TABS.map((tab) => {
          const count = getTabCount(tab);
          return (
            <button
              key={tab.id}
              className={`wfe-tab ${state.activeTab === tab.id ? 'active' : ''}`}
              onClick={() => dispatch({ type: 'SET_TAB', tab: tab.id })}
            >
              {tab.label}
              {count !== null && <span className="wfe-tab-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {state.activeTab === 'metadata' && (
        <MetadataSection config={state.config} dispatch={dispatch} />
      )}

      {state.activeTab === 'extraction' && (
        <ExtractionSection
          config={state.config}
          allFields={state.allFields}
          allConfigs={state.allConfigs || []}
          dispatch={dispatch}
        />
      )}

      {state.activeTab === 'redflags' && (
        <RedFlagsSection
          config={state.config}
          allFlags={state.allFlags}
          allConfigs={state.allConfigs || []}
          dispatch={dispatch}
        />
      )}

      {state.activeTab === 'documents' && (
        <DocumentTemplatesSection
          config={state.config}
          allTemplates={state.allTemplates}
          allConfigs={state.allConfigs || []}
          dispatch={dispatch}
        />
      )}

      {state.activeTab === 'preview' && (
        <PreviewSection
          config={state.config}
          allFields={state.allFields}
          allFlags={state.allFlags}
          allTemplates={state.allTemplates}
        />
      )}

      {/* Unsaved changes confirmation dialog — rendered via portal */}
      {showDiscardConfirm && createPortal(
        <div className="wfe-confirm-overlay" onClick={handleCancelLeave}>
          <div className="wfe-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>You have unsaved changes</h3>
            <p>Your changes to this workflow configuration will be lost.</p>
            <div className="wfe-confirm-actions">
              <button className="wfe-btn-discard" onClick={handleCancelLeave}>
                Stay on Page
              </button>
              <button className="wfe-btn-danger" onClick={handleConfirmLeave}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default WorkflowEditor;
